import { Component, ElementRef, ViewChild, Input, AfterViewInit, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Chart, ChartConfiguration, Plugin, registerables } from 'chart.js';

@Component({
    selector: 'app-gauge-chart',
    templateUrl: './gauge-chart.component.html',
    styleUrls: ['./gauge-chart.component.scss'],
    standalone: false
})
export class GaugeChartComponent implements AfterViewInit, OnDestroy, OnChanges {
  
  @ViewChild('chartCanvas') chartCanvas!: ElementRef<HTMLCanvasElement>;
  @Input() needleValue: number = 0;
  @Input() chartLabel: string = '';
  @Input() chartBottomText: string = '';

  chart: Chart<"doughnut", number[], unknown> | undefined;

  centerX: number = 0;
  centerY: number = 0;

  private currentRotation: number = 0;
  private animationId: number | null = null;

  private readonly animationDuration: number = 3000;
  private readonly CHART_START_ANGLE = (210 / 360) * 2 * Math.PI;
  private readonly CHART_TOTAL_ANGLE = (300 / 360) * 2 * Math.PI;

  constructor() {
    Chart.register(...registerables);
  }

  ngAfterViewInit() {
    this.initChart();
    this.animateNeedle();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['needleValue'] && !changes['needleValue'].firstChange) {
      this.updateChart();
    }
  }

  private updateChart(): void {
    if (this.chart) {
      this.animateNeedle();
      this.chart.update('none');
    }
  }

  private initChart(): void {
    if (!this.chartCanvas) {
      console.error('Canvas element not found');
      return;
    }

    const ctx = this.chartCanvas.nativeElement.getContext('2d');
    if (!ctx) {
      console.error('Unable to get 2D context for canvas');
      return;
    }

    const config: ChartConfiguration<'doughnut'> = {
      type: 'doughnut',
      data: {
        datasets: [{
          label: 'Location Performance',
          data: new Array(120).fill(10),
          backgroundColor: (context: { chart: any; dataIndex: number; }) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) {
              return;
            }
            this.centerX = (chartArea.left + chartArea.right) / 2;
            this.centerY = (chartArea.top + chartArea.bottom) / 2;
            return this.createGradient(ctx, chartArea);
          },
          circumference: 300,
          rotation: 210
        }]
      },
      options: {
        cutout: '68%',
        plugins: {
          tooltip: { enabled: false },
          legend: { display: false }
        },
        layout: {
          padding: {
            bottom: 15
          }
        },
        hover: { mode: null as any },
        animation: { duration: 0 }
      },
      plugins: [this.createTicksPlugin(), this.createGaugeNeedleValue()]
    };

    try {
      this.chart = new Chart(ctx, config);
    } catch (error) {
      console.error('Error creating chart:', error);
    }
  }

  /**
   *@description Create a gradient for the donut chart
   *
   * @param ctx  CanvasRenderingContext2D
   * @param chartArea ChartArea
   * @returns Gradient colors for the donut chart
  */
   createGradient(ctx: CanvasRenderingContext2D, chartArea: { left: number; right: number; top: number; bottom: number }): CanvasGradient {
    // Calculate the center of the chart
    const centerX = (chartArea.left + chartArea.right) / 2;
    const centerY = (chartArea.top + chartArea.bottom) / 2;

    // Convert degrees to radians and subtract 100 to align with CSS angles
    const startAngle = (210 - 100) * (Math.PI / 180);
    const gradient = ctx.createConicGradient(startAngle, centerX, centerY);

    gradient.addColorStop(0, "#F34757"); //red
    gradient.addColorStop(0.5, "#F0DC81"); //yellow
    gradient.addColorStop(1, "#1AC584"); // green

    return gradient;
  }

  /**
   *@description Create a plugin to draw the gauge needle and value
   *
   * @returns Plugin to draw the gauge needle and value
   */
   createGaugeNeedleValue(): Plugin<"doughnut"> {
    return {
      id: 'gaugeNeedle',
      afterDatasetsDraw: (chart: any) => {
        const { ctx, data } = chart;
        ctx.save();

        const xCenter = chart.getDatasetMeta(0).data[0].x;
        const yCenter = chart.getDatasetMeta(0).data[0].y;
        const outerRadius = (chart.getDatasetMeta(0).data[0] as any).outerRadius - 20;

        this.drawNeedle(ctx, xCenter, yCenter, outerRadius);
        this.drawNeedleValue(ctx, xCenter, yCenter);
      }
    };
  }

  private drawNeedle(ctx: CanvasRenderingContext2D, xCenter: number, yCenter: number, outerRadius: number): void {
    ctx.save();
    ctx.translate(xCenter, yCenter);
    ctx.rotate(this.currentRotation);

    ctx.beginPath();
    ctx.strokeStyle = '#333333';
    ctx.fillStyle = '#333333';
    ctx.moveTo(-3, 0);
    ctx.lineTo(0, -outerRadius - 10);
    ctx.lineTo(3, 0);
    ctx.stroke();
    ctx.fill();

    ctx.beginPath();
    ctx.arc(0, 0, 3, 0, Math.PI * 2, false);
    ctx.fill();

    ctx.restore();
  }

  private drawNeedleValue(ctx: CanvasRenderingContext2D, xCenter: number, yCenter: number): void {
    ctx.font = '600 40px Roboto';
    ctx.fillStyle = '#006BD3';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.needleValue.toString(), xCenter, yCenter * 1.9);
  }


  /**
   *@description Create a plugin to draw the gauge ticks (15 ticks - can be changed as required)
   *
   * @returns Plugin to draw the gauge ticks
  */
  createTicksPlugin(): Plugin<"doughnut"> {
    return {
      id: 'gaugeTicks',
      beforeDraw(chart: Chart<"doughnut">, args, options) {
        const { ctx, width, height } = chart;

        const centerX = width / 2;
        const centerY = height / 2 + 10;
        const outerRadius = (chart.getDatasetMeta(0).data[0] as any).outerRadius;

        // Variables to control tick positioning and length
        const tickOuterRadius = outerRadius * .55; // Start ticks inside the inner edge
        const tickLength = 5;
        const tickInnerRadius = tickOuterRadius - tickLength;

        ctx.save();
        ctx.strokeStyle = '#333333'; // Color of the ticks
        ctx.lineWidth = 1; // Width of the ticks

        // Calculate start and end angles (in radians)
        const startAngle = Math.PI * 0.75; // Start at 7 o'clock
        const endAngle = Math.PI * 2.25; // End at 5 o'clock
        const angleStep = (endAngle - startAngle) / 14; // 14 steps for 15 ticks

        for (let i = 0; i <= 14; i++) {
          const angle = startAngle + (i * angleStep);

          const innerX = centerX + tickInnerRadius * Math.cos(angle);
          const innerY = centerY + tickInnerRadius * Math.sin(angle);
          const outerX = centerX + tickOuterRadius * Math.cos(angle);
          const outerY = centerY + tickOuterRadius * Math.sin(angle);

          ctx.beginPath();
          ctx.moveTo(innerX, innerY);
          ctx.lineTo(outerX, outerY);
          ctx.stroke();
        }

        ctx.restore();
      }
    };
  }

  animateNeedle(duration: number = this.animationDuration) {

    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
    }

    const startTime = performance.now();
    const targetAngle = (this.needleValue / 100) * this.CHART_TOTAL_ANGLE;

    const animate = (currentTime: number): void => {
      const elapsedTime = currentTime - startTime;
      const progress = Math.min(elapsedTime / duration, 1);

      // Ease out cubic function
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      this.currentRotation = this.CHART_START_ANGLE + easedProgress * targetAngle;

      if (this.chart) {
        this.chart.update('none');
      }

      if (progress < 1) {
        this.animationId = requestAnimationFrame(animate);
      }
    };

    this.animationId = requestAnimationFrame(animate);
  }

  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
    }

    if (this.animationId !== null) {
      cancelAnimationFrame(this.animationId);
    }
  }
}
